import React from "react";
import { useTranslation } from "react-i18next";

import {
    Card,
    CardHeader,
    CardHeaderTitle,
    FilterRow,
    FilterLabel,
    SmallToggle,
    ColorSwatch,
    ColorValue,
    ResetIconButton,
} from "./styled";
import { ResetIcon } from "./ResetIcon";
import { FilterKey, FilterConfig } from "./types";
import { ColorPickerPopover } from "../shared/ColorPickerPopover";

type FiltersCardProps = {
    filters: Record<FilterKey, FilterConfig>;
    mainColor: string;
    activeColorPicker: string | null;
    setActiveColorPicker: (key: string | null) => void;
    onUpdateFilter: (key: FilterKey, updates: Partial<FilterConfig>) => void;
    onReset: () => void;
};

export function FiltersCard({
    filters,
    mainColor,
    activeColorPicker,
    setActiveColorPicker,
    onUpdateFilter,
    onReset,
}: FiltersCardProps): JSX.Element {
    const { t } = useTranslation();

    const FILTER_LABELS: Record<FilterKey, string> = {
        all: t("All"),
        pending: t("Pending"),
        accepted: t("Accepted"),
        ready: t("Ready"),
        refused: t("Refused"),
    };

    return (
        <Card>
            <CardHeader>
                <CardHeaderTitle>{t("Status filters")}</CardHeaderTitle>
                <ResetIconButton
                    data-testid="servio-commandes-reset-filters-btn"
                    mainColor={mainColor}
                    onClick={onReset}
                    title={t("Reset to default")}
                >
                    <ResetIcon />
                    {t("Reset")}
                </ResetIconButton>
            </CardHeader>
            {(Object.keys(FILTER_LABELS) as FilterKey[]).map((key) => (
                <FilterRow
                    key={key}
                    data-testid={`servio-commandes-filter-${key}`}
                >
                    <SmallToggle
                        data-testid={`servio-commandes-filter-${key}-toggle`}
                        isActive={filters[key].enabled}
                        mainColor={mainColor}
                        onClick={() =>
                            onUpdateFilter(key, {
                                enabled: !filters[key].enabled,
                            })
                        }
                    />
                    <FilterLabel>{FILTER_LABELS[key]}</FilterLabel>
                    <ColorSwatch
                        data-testid={`servio-commandes-filter-${key}-color`}
                        color={filters[key].color}
                        onClick={(e) => {
                            // Safari does not auto-focus buttons on click;
                            // forcing it ensures useDialogA11y's previousFocus
                            // captures the swatch so focus is restored here
                            // when the picker closes.
                            e.currentTarget.focus();
                            setActiveColorPicker(
                                activeColorPicker === key ? null : key
                            );
                        }}
                    />
                    <ColorValue>{filters[key].color}</ColorValue>
                    {activeColorPicker === key && (
                        <ColorPickerPopover
                            color={filters[key].color}
                            onChange={(hex) =>
                                onUpdateFilter(key, { color: hex })
                            }
                            onClose={() => setActiveColorPicker(null)}
                            data-testid={`servio-commandes-filter-${key}-color-picker`}
                        />
                    )}
                </FilterRow>
            ))}
        </Card>
    );
}
